<script setup lang="ts">
import { useRouter } from 'vue-router'
import PageFooter from './PageFooter.vue'
import { useRealtimeStore } from '@/stores/realtime'
import { PhChartPie, PhChatCircle, PhCompass, PhHouse, PhPlus } from '@phosphor-icons/vue'

defineOptions({ name: 'AppTabbar' })

const props = defineProps<{
  active: 'home' | 'browse' | 'inbox' | 'dashboard'
}>()

const leftTabs = [
  { id: 'home', label: 'Home', icon: PhHouse, path: '/' },
  { id: 'browse', label: 'Browse', icon: PhCompass, path: '/browse/' },
] as const

const rightTabs = [
  { id: 'inbox', label: 'Inbox', icon: PhChatCircle, path: '/inbox/' },
  { id: 'dashboard', label: 'Dashboard', icon: PhChartPie, path: '/dashboard/' },
] as const

const router = useRouter()
const realtime = useRealtimeStore()

// New messages and order updates both surface in the inbox.
function badgeFor(tabId: string) {
  return tabId === 'inbox' ? realtime.unreadCount : 0
}

function onTab(path: string) {
  if (router.currentRoute.value.path === path) return
  router.push(path)
}
</script>

<template>
  <PageFooter kind="tabs" class="app-tabbar md:hidden">
    <div class="flex h-[72px] w-full items-center justify-between">
      <button
        v-for="tab in leftTabs"
        :key="tab.id"
        type="button"
        class="flex w-16 flex-col items-center gap-1 transition-colors touch-manipulation cursor-pointer select-none"
        :class="props.active === tab.id ? 'text-accent-cyan' : 'text-ink-5 active:text-ink-3'"
        :aria-current="props.active === tab.id ? 'page' : undefined"
        @click="onTab(tab.path)"
      >
        <component :is="tab.icon" class="h-5 w-5" />
        <span class="text-[10px] font-medium">{{ tab.label }}</span>
      </button>

      <button
        type="button"
        class="-mt-7 flex h-12 w-12 shrink-0 items-center justify-center rounded-full border-4 border-canvas bg-cta text-cta-foreground shadow-[0_0_15px_rgba(37,227,233,0.4)] transition-transform active:scale-95 touch-manipulation cursor-pointer select-none"
        aria-label="Create a new listing"
        @click="onTab('/create-listing/')"
      >
        <PhPlus class="h-5 w-5" weight="bold" />
      </button>

      <button
        v-for="tab in rightTabs"
        :key="tab.id"
        type="button"
        class="flex w-16 flex-col items-center gap-1 transition-colors touch-manipulation cursor-pointer select-none"
        :class="props.active === tab.id ? 'text-accent-cyan' : 'text-ink-5 active:text-ink-3'"
        :aria-current="props.active === tab.id ? 'page' : undefined"
        @click="onTab(tab.path)"
      >
        <span class="relative">
          <component :is="tab.icon" class="h-5 w-5" />
          <span
            v-if="badgeFor(tab.id)"
            class="absolute -right-2.5 -top-1.5 min-w-4 h-4 rounded-full bg-cta px-1 text-center text-[10px] font-bold leading-4 text-cta-foreground ring-2 ring-canvas"
          >
            {{ badgeFor(tab.id) > 9 ? '9+' : badgeFor(tab.id) }}
          </span>
        </span>
        <span class="text-[10px] font-medium">{{ tab.label }}</span>
        <span v-if="badgeFor(tab.id)" class="sr-only">({{ badgeFor(tab.id) }} unread)</span>
      </button>
    </div>
  </PageFooter>
</template>
